/**
 * Theme Context Provider
 * 
 * Manages theme state and dark/light mode preferences.
 * Uses CSS variables for efficient theme switching.
 */

import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { Experimental_CssVarsProvider as CssVarsProvider, useColorScheme, useTheme as useMuiTheme } from '@mui/material/styles';
import { CssBaseline, GlobalStyles } from '@mui/material';
import { buildTheme, getPreferredThemeMode, setThemeMode } from '../themes/themeBuilder.js';

// Create the theme context
const ThemeContext = createContext();

/**
 * Custom hook to use the theme context
 * @returns {Object} Theme context value
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

/**
 * Inner Theme Provider Component that uses useColorScheme
 * This component must be inside the CssVarsProvider to access useColorScheme
 */
function ThemeProviderInner({ children }) {
  const { mode, setMode } = useColorScheme();
  const [isLoading, setIsLoading] = useState(false);
  
  const isDarkMode = mode === 'dark';

  /**
   * Toggle between dark and light mode
   */
  const toggleDarkMode = () => {
    const newMode = isDarkMode ? 'light' : 'dark';
    setMode(newMode);
  };

  /**
   * Set dark mode explicitly
   * @param {boolean} darkMode - Whether to enable dark mode
   */
  const setDarkMode = (darkMode) => {
    setMode(darkMode ? 'dark' : 'light');
  };

  /**
   * Reset theme to system preference
   */
  const resetToSystemTheme = () => {
    setMode('system');
  };

  // Context value
  const contextValue = {
    // Current state
    isDarkMode,
    isLoading,
    theme: null, // Theme is now managed by CssVarsProvider
    
    // Actions
    toggleDarkMode,
    setDarkMode,
    resetToSystemTheme
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <CssBaseline />
      <GlobalStyles
        styles={{
          ':root': {
            // Add transition to all CSS variables for smooth theme changes
            transition: 'all 0.3s ease',
          },
          body: {
            // Use CSS variables for background color
            backgroundColor: 'var(--bg-page-default)',
            transition: 'background-color 0.3s ease',
          },
          '#root': {
            // Use CSS variables for background color
            minHeight: '100dvh',
          }
        }}
      />
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Theme Provider Component
 * Wraps the application with theme management capabilities
 */
export function ThemeProvider({ children }) {
  // Build the theme once (no longer dependent on mode)
  const theme = useMemo(() => {
    return buildTheme(); // Don't pass isDarkMode, let CssVarsProvider handle it
  }, []);

  const defaultMode = getPreferredThemeMode() ? 'dark' : 'light';

  return (
    <CssVarsProvider 
      theme={theme}
      defaultMode={defaultMode}
      modeStorageKey="themeMode"
    >
      <ThemeProviderInner>
        {children}
      </ThemeProviderInner>
    </CssVarsProvider>
  );
}

/**
 * Higher-order component to provide theme context
 * @param {React.Component} Component - Component to wrap
 * @returns {React.Component} Wrapped component with theme context
 */
export function withTheme(Component) {
  return function ThemedComponent(props) {
    return (
      <ThemeProvider>
        <Component {...props} />
      </ThemeProvider>
    );
  };
}

/**
 * Hook to get theme-aware styles
 * @returns {Object} Style utilities including CSS variables
 */
export function useThemeStyles() {
  const { isDarkMode } = useTheme();
  const muiTheme = useMuiTheme(); // Get theme from MUI context
  
  return {
    theme: muiTheme,
    isDarkMode,
    
    // Common style utilities
    getContrastText: (backgroundColor) => {
      return muiTheme.palette.getContrastText(backgroundColor);
    },
    
    // Spacing utilities
    spacing: muiTheme.spacing,
    
    // Breakpoint utilities
    breakpoints: muiTheme.breakpoints,
    
    // Color utilities
    colors: muiTheme.palette,
    
    // CSS variables utilities
    vars: muiTheme.vars,
    
    // Custom theme variables
    customVars: {
      // Gradients
      gradient: muiTheme.vars?.palette?.gradient,
      
      // Border styles
      border: muiTheme.vars?.palette?.border,
      
      // Surface styles
      surface: muiTheme.vars?.palette?.surface,
      
      // Overlay styles
      overlay: muiTheme.vars?.palette?.overlay
    },
    
    // Helper for using CSS variables in inline styles
    cssVar: (path) => {
      // Example: cssVar('palette.primary.main') returns 'var(--mui-palette-primary-main)'
      const varPath = path.replace(/\./g, '-');
      return `var(--mui-${varPath})`;
    }
  };
}
